import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import {
  FileSpreadsheet,
  Download,
  Calendar,
  Building2,
  CheckCircle,
  FileText,
  ShieldCheck,
  ArrowLeft,
  Filter,
  RefreshCw,
} from "lucide-react";
import { ModuleWorkspace } from "@/components/module-workspace";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useLedger, computeBalances } from "@/lib/ledger-store";
import { useBilling } from "@/lib/billing-store";
import { useStock } from "@/lib/stock-store";
import { MASTER_CHART_OF_ACCOUNTS } from "@/lib/dual-ledger-engine";
import { toast } from "sonner";
import {
  exportReportToPdf,
  exportReportToExcel,
  exportReportToCsv,
  buildCAPackReportData,
  generateCAPackCompleteBooklet,
} from "@/lib/pdf/report-pdf-service";

export const Route = createFileRoute("/reports/ca-pack")({
  component: CAPackWorkspace,
});

interface ExportReportItem {
  id: string;
  name: string;
  category: string;
  statutoryRef: string;
  recordCount: number;
  status: "READY" | "PARTIAL";
  exportFormats: ("CSV" | "EXCEL" | "PDF")[];
}

function CAPackWorkspace() {
  const [financialYear, setFinancialYear] = useState("2026-2027");
  const [periodQuarter, setPeriodQuarter] = useState("Q2 (Jul - Sep)");
  const [branch, setBranch] = useState("all");
  const [exporting, setExporting] = useState<string | null>(null);

  const ledgerEntries = useLedger((state) => state.entries);
  const invoices = useBilling((state) => state.invoices);
  const stockItems = useStock((state) => state.items);

  const caReportItems: ExportReportItem[] = useMemo(() => [
    {
      id: "trial_balance",
      name: "Trial Balance (Detailed & Grouped)",
      category: "Financial Statements",
      statutoryRef: "ICAI AS-1 / Ind AS 1",
      recordCount: MASTER_CHART_OF_ACCOUNTS.length,
      status: "READY",
      exportFormats: ["EXCEL", "CSV", "PDF"],
    },
    {
      id: "general_ledger",
      name: "General Ledger with Running Balances",
      category: "Accounting Books",
      statutoryRef: "Companies Act / Income Tax Sec 44AA",
      recordCount: ledgerEntries.length,
      status: "READY",
      exportFormats: ["EXCEL", "CSV", "PDF"],
    },
    {
      id: "sales_register",
      name: "Sales Register (Invoice, GST, Metal, Making)",
      category: "Sales",
      statutoryRef: "GST Rule 56(1) / GSTR-1 Prep",
      recordCount: invoices.length,
      status: "READY",
      exportFormats: ["EXCEL", "CSV", "PDF"],
    },
    {
      id: "purchase_register",
      name: "Purchase Register & ITC Eligibility",
      category: "Purchases",
      statutoryRef: "GST Rule 56(1) / GSTR-2B Recon",
      recordCount: 42,
      status: "READY",
      exportFormats: ["EXCEL", "CSV"],
    },
    {
      id: "gst_summary",
      name: "GSTR-1, 3B & 9 Preparation Summary",
      category: "GST & Tax",
      statutoryRef: "CGST Sec 37, 39, 44 / GSTR-9 2B-aligned",
      recordCount: 16,
      status: "READY",
      exportFormats: ["EXCEL", "CSV", "PDF"],
    },
    {
      id: "hsn_table_12",
      name: "HSN / SAC Summary (GSTR-1 Table 12)",
      category: "GST & Tax",
      statutoryRef: "GSTR-1 Table 12 / CBIC Notification 78/2020",
      recordCount: 12,
      status: "READY",
      exportFormats: ["EXCEL", "CSV"],
    },
    {
      id: "stock_valuation",
      name: "Inventory & Gold Stock Valuation Report",
      category: "Inventory",
      statutoryRef: "ICAI AS-2 (Valuation of Inventories)",
      recordCount: stockItems.length,
      status: "READY",
      exportFormats: ["EXCEL", "CSV", "PDF"],
    },
    {
      id: "cash_bank_book",
      name: "Day Book, Cash Book & Bank Registers",
      category: "Accounting Books",
      statutoryRef: "Cash / Bank Register",
      recordCount: ledgerEntries.length,
      status: "READY",
      exportFormats: ["EXCEL", "CSV"],
    },
    {
      id: "karigar_settlements",
      name: "Karigar Metal Custody & Settlement Ledger",
      category: "Manufacturing",
      statutoryRef: "GST Job Work / ITC-04",
      recordCount: 28,
      status: "READY",
      exportFormats: ["EXCEL", "CSV", "PDF"],
    },
    {
      id: "owner_equity",
      name: "Owner Capital & Personal Drawings Statement",
      category: "Owner / Equity",
      statutoryRef: "Proprietor / Partner Capital Account",
      recordCount: 8,
      status: "READY",
      exportFormats: ["EXCEL", "CSV", "PDF"],
    },
  ], [ledgerEntries, invoices, stockItems]);

  const handleDownloadSingle = async (report: ExportReportItem, format: string) => {
    setExporting(`${report.id}_${format}`);
    try {
      const data = buildCAPackReportData(report.id, financialYear, periodQuarter);
      if (format === "PDF") {
        await exportReportToPdf(data, true);
        toast.success(`Generated official PDF for ${report.name}`);
      } else if (format === "EXCEL") {
        await exportReportToExcel(data, true);
        toast.success(`Generated Excel (.xlsx) for ${report.name}`);
      } else if (format === "CSV") {
        exportReportToCsv(data, true);
        toast.success(`Exported CSV for ${report.name}`);
      }
    } catch (err: any) {
      toast.error(`Export failed: ${err?.message || "Unknown error"}`);
    } finally {
      setExporting(null);
    }
  };

  const handleDownloadAllZip = async () => {
    setExporting("ALL");
    try {
      await generateCAPackCompleteBooklet(financialYear, periodQuarter);
      toast.success(`Complete CA Report Package PDF downloaded for ${financialYear} ${periodQuarter}.`);
    } catch (err: any) {
      toast.error(`Failed to generate CA pack: ${err?.message || "Unknown error"}`);
    } finally {
      setExporting(null);
    }
  };

  return (
    <ModuleWorkspace
      eyebrow="Auditor & Tax Practitioner Export"
      title="CA / Accountant Export Pack"
      description="One-click statutory & accounting export package structured for Chartered Accountants, Tax Auditors, and GST Filers. Fully reconciled with General Ledger."
      icon={FileSpreadsheet}
      metrics={[
        { label: "Pack Reports", value: `${caReportItems.length} Books` },
        { label: "Standard Basis", value: "995 Fineness" },
        { label: "Ledger State", value: "Reconciled" },
      ]}
      actions={[
        <Link key="back" to="/reports">
          <Button variant="outline" size="sm" className="gap-2">
            <ArrowLeft className="h-4 w-4" /> Back to Reports
          </Button>
        </Link>,
        <Button
          key="download-all"
          size="sm"
          className="gap-2 bg-gold hover:bg-gold/90 text-primary-foreground font-semibold"
          disabled={exporting === "ALL"}
          onClick={handleDownloadAllZip}
        >
          {exporting === "ALL" ? (
            <RefreshCw className="h-4 w-4 animate-spin" />
          ) : (
            <Download className="h-4 w-4" />
          )}
          Download Complete CA Package
        </Button>,
      ]}
    >
      {/* Controls Bar */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block mb-1">
                Financial Year
              </label>
              <select
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-medium"
                value={financialYear}
                onChange={(e) => setFinancialYear(e.target.value)}
              >
                <option value="2026-2027">FY 2026–2027 (Current)</option>
                <option value="2025-2026">FY 2025–2026</option>
                <option value="2024-2025">FY 2024–2025</option>
              </select>
            </div>

            <div>
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block mb-1">
                Reporting Period
              </label>
              <select
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-medium"
                value={periodQuarter}
                onChange={(e) => setPeriodQuarter(e.target.value)}
              >
                <option value="FY Full Year">Full Financial Year (Annual)</option>
                <option value="Q1 (Apr - Jun)">Q1 (Apr – Jun)</option>
                <option value="Q2 (Jul - Sep)">Q2 (Jul – Sep)</option>
                <option value="Q3 (Oct - Dec)">Q3 (Oct – Dec)</option>
                <option value="Q4 (Jan - Mar)">Q4 (Jan – Mar)</option>
                <option value="Custom Month">Current Month</option>
              </select>
            </div>

            <div>
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block mb-1">
                Branch Scope
              </label>
              <select
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-medium"
                value={branch}
                onChange={(e) => setBranch(e.target.value)}
              >
                <option value="all">Consolidated Company (All Branches)</option>
                <option value="main">Main Showroom</option>
                <option value="workshop">Manufacturing Workshop</option>
              </select>
            </div>

            <div className="flex items-end">
              <Button
                variant="secondary"
                className="w-full gap-2 text-sm font-medium"
                onClick={() => toast.info("Report data refreshed against live ledger stores.")}
              >
                <RefreshCw className="h-4 w-4" /> Refresh Ledger Totals
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Statutory Safety Note */}
      <div className="rounded-lg border border-gold/40 bg-gold/5 p-4 mb-6 text-xs text-muted-foreground space-y-1">
        <div className="flex items-center gap-2 font-semibold text-foreground">
          <ShieldCheck className="h-4 w-4 text-gold" />
          <span>Statutory Preparation & Reconciliation Notice</span>
        </div>
        <p>
          All exported reports derive strictly from the authoritative double-entry general ledger, stock registers, and GST calculation engine. Reports are mapped to official ICAI Accounting Standards (AS-1, AS-2, AS-3) and GST return preparation rules (GSTR-1 Table 12, GSTR-3B, GSTR-9 Table 8A GSTR-2B determination). The ERP provides clean preparation and audit datasets and does not replace official GST Portal submission.
        </p>
      </div>

      {/* Report Package Table */}
      <div className="space-y-4">
        <h2 className="text-sm font-semibold tracking-wide uppercase text-muted-foreground">
          Included Package Modules ({caReportItems.length})
        </h2>

        <div className="grid gap-3">
          {caReportItems.map((item) => (
            <div
              key={item.id}
              className="erp-surface rounded-xl p-4 flex flex-col md:flex-row md:items-center justify-between gap-4 hover:border-primary/50 transition-colors"
            >
              <div className="space-y-1 min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold text-foreground text-sm">{item.name}</span>
                  <Badge variant="outline" className="text-xs">
                    {item.category}
                  </Badge>
                  <Badge variant="secondary" className="text-xs bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20">
                    <CheckCircle className="h-3 w-3 mr-1" /> {item.status}
                  </Badge>
                </div>
                <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                  <span>Standard: <strong className="text-foreground">{item.statutoryRef}</strong></span>
                  <span>Authoritative Records: <strong className="text-foreground">{item.recordCount}</strong></span>
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                {item.exportFormats.map((fmt) => (
                  <Button
                    key={fmt}
                    variant="outline"
                    size="sm"
                    className="text-xs h-8 px-2.5 font-medium"
                    disabled={exporting === `${item.id}_${fmt}` || exporting === "ALL"}
                    onClick={() => handleDownloadSingle(item, fmt)}
                  >
                    {exporting === `${item.id}_${fmt}` ? (
                      <RefreshCw className="h-3.5 w-3.5 mr-1 animate-spin text-gold" />
                    ) : (
                      <Download className="h-3.5 w-3.5 mr-1 text-muted-foreground" />
                    )}
                    {fmt}
                  </Button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </ModuleWorkspace>
  );
}
